import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, unref } from 'vue';
import { usePositionHistory } from '@/composables/usePositionHistory.ts';
import { useStopHistory } from '@/composables/useStopHistory.ts';
import { usePosition } from '@/composables/usePosition.ts';
import { useDevice } from '@/composables/useDevice.ts';
import { useFilter } from '@/composables/useFilter.ts';
import { useGroup } from '@/composables/useGroup.ts';
import { filterDeviceIds } from '@/utils/device-filter.ts';
import { useSearchStore } from '@/stores/searchStore';
import { useTrackersListStatusStore } from '@/stores/useTrackersListStatusStore';
import isEmpty from 'lodash/isEmpty';
import { useRoute } from 'vue-router';

export type TViewType = 'deviceView' | 'groupList' | 'groupDetail';

export const useMap = defineStore('map', () => {
  const savedMapSettings = ref({zoom: null, center: null});
  const viewType = ref<TViewType>('deviceView');
  const isAutoRefreshing = ref(false);

  const route = useRoute();
  const positionHistoryStore = usePositionHistory();
  const stopHistory = useStopHistory();
  const positionStore = usePosition();
  const deviceStore = useDevice();
  const groupStore = useGroup();
  const filterStore = useFilter();
  const searchStore = useSearchStore();
  const statusStore = useTrackersListStatusStore();

  const { isLoading: isLoadingDevices, positions } = storeToRefs(positionStore);
  const {
    selectedIds,
    filter,
    devices,
    resultDeviceIds,
  } = storeToRefs(deviceStore);

  const { dateFilter } = storeToRefs(filterStore);

  const { resultDeviceIds: resultGroupDeviceIds, isLoading: isLoadingGroups } =
    storeToRefs(groupStore);

  const {
    getHistoryPositionsByDateForDeviceId,
    getHistoryPositionsByDateForAllDevices,
  } = positionHistoryStore;
  const { getStopHistoryByDateForDeviceId } = stopHistory;

  const isLoading = computed(
    () => isLoadingGroups.value || isLoadingDevices.value,
  );

  const filteredGroupDeviceIds = computed(() =>
    filterDeviceIds(resultGroupDeviceIds.value, devices.value, {
      search: searchStore.normalizedQuery,
      filter: filter.value,
      selectedStatuses: statusStore.getSelectedStatuses('groupDetail'),
    }),
  );

  const computedDevices = computed(() => {
    if (viewType.value === 'deviceView') {
      if (selectedIds.value.length) {
        return selectedIds.value;
      }
      return resultDeviceIds.value;
    }

    let resultIds = [];
    if (viewType.value === 'groupList') {
      resultIds = resultGroupDeviceIds.value;
    }

    if (viewType.value === 'groupDetail') {
      if (selectedIds.value.length) {
        resultIds = selectedIds.value;
      } else {
        resultIds = filteredGroupDeviceIds.value;
      }
    }

    return resultIds;
  });

  const computedPositions = computed(() => {
    const result = [];
    computedDevices.value.forEach((deviceId) => {
      const position = positions.value.get(deviceId);
      if (position) {
        result.push(position);
      }
    });
    return result;
  });

  const clusterPositionsHistory = computed(() => {
    let positions = getHistoryPositionsByDateForAllDevices();
    if (viewType.value === 'deviceView') {
      if (resultDeviceIds.value.length > 0) {
        positions = positions.filter((position) =>
          resultDeviceIds.value.includes(position.id),
        );
      }
      return !selectedIds.value.length
        ? positions
        : positions.filter((position) =>
            selectedIds.value.includes(position.id),
          );
    }
    if (viewType.value === 'groupList') {
      return positions.filter((item) =>
        resultGroupDeviceIds.value.includes(item.id),
      );
    }
    if (viewType.value === 'groupDetail') {
      const scopedIds = isEmpty(selectedIds.value)
        ? filteredGroupDeviceIds.value
        : selectedIds.value;
      const historyPositions = getHistoryPositionsByDateForAllDevices().filter(
        (item) => scopedIds.includes(item.id),
      );
      return historyPositions;
    }
    return [];
  });

  const singlePositionsHistory = computed(() =>
    getHistoryPositionsByDateForDeviceId(
      +route.params.id,
      unref(dateFilter).dateFrom,
      unref(dateFilter).dateTo,
    ),
  );
  const stopsHistory = computed(() =>
    getStopHistoryByDateForDeviceId(
      +route.params.id,
      unref(dateFilter).dateFrom,
      unref(dateFilter).dateTo,
    ),
  );

  return {
    viewType,
    isLoading,
    computedPositions,
    computedDevices,
    singlePositionsHistory,
    clusterPositionsHistory,
    stopsHistory,
    isAutoRefreshing,
    savedMapSettings,
    setViewType: (_viewType: TViewType) => (viewType.value = _viewType),
  };
});
